import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { totpVerifySchema, type TotpVerifyInput } from '@dcp/shared'
import {
  AsyncButton,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
} from '@atrvd/saas-ui'
import { supabase } from '@/lib/supabase'

/**
 * Props for the MFAVerifyStep component.
 */
interface MFAVerifyStepProps {
  /** Called after successful MFA verification */
  onVerified: () => void
  /** Called when user wants to go back to login form */
  onBack: () => void
}

/**
 * MFA verification step shown on the login page when the user has TOTP enabled.
 * Replaces the login form, prompts for a 6-digit TOTP code, and completes
 * the MFA challenge via Supabase Auth before calling `onVerified`.
 *
 * On mount, fetches the user's verified TOTP factor from Supabase and stores
 * the factor ID for the subsequent challengeAndVerify call.
 */
export function MFAVerifyStep({ onVerified, onBack }: MFAVerifyStepProps) {
  /** ID of the first verified TOTP factor for the current user. */
  const [factorId, setFactorId] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<TotpVerifyInput>({
    resolver: zodResolver(totpVerifySchema),
    defaultValues: {
      code: '',
    },
  })

  /**
   * Fetch the user's verified TOTP factors on mount and store the first factor ID.
   * If Supabase is not configured or no verified TOTP factor exists, the submit
   * handler will handle the null factorId gracefully with an error message.
   */
  useEffect(() => {
    if (!supabase) return

    supabase.auth.mfa
      .listFactors()
      .then(({ data }) => {
        const totp = data?.totp?.find((f) => f.status === 'verified')
        if (totp) {
          setFactorId(totp.id)
        }
      })
      .catch((error) => {
        // Non-critical — submit handler will surface the error if factorId is null
        console.error('Failed to fetch MFA factors on mount:', error)
      })
  }, [])

  /**
   * Handle TOTP form submission.
   * Issues a challengeAndVerify call to Supabase MFA and calls `onVerified` on success.
   * Maps known Supabase error messages to user-friendly text.
   *
   * @param data - Validated form values containing the 6-digit code
   */
  const onSubmit = async (data: TotpVerifyInput) => {
    if (!supabase || !factorId) {
      setError('root', { message: 'MFA factor not available. Please try signing in again.' })
      return
    }

    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId,
      code: data.code,
    })

    if (error) {
      const message = error.message.includes('Invalid')
        ? 'Invalid verification code. Please try again.'
        : error.message
      setError('root', { message })
      return
    }

    onVerified()
  }

  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">Two-Factor Authentication</CardTitle>
        <CardDescription className="text-center">
          Enter the 6-digit code from your authenticator app
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="code">Verification Code</Label>
            <Input
              id="code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
              placeholder="000000"
              {...register('code')}
              aria-invalid={!!errors.code}
            />
            {errors.code && (
              <p className="text-sm text-destructive">{errors.code.message}</p>
            )}
          </div>

          <AsyncButton
            type="submit"
            className="w-full"
            isLoading={isSubmitting}
            loadingText="Verifying..."
          >
            Verify
          </AsyncButton>

          <button
            type="button"
            onClick={onBack}
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground mt-2"
          >
            Back to login
          </button>
        </form>
      </CardContent>
    </Card>
  )
}
